/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, defer, tap, throwError } from 'rxjs';
import { AtlasRequest } from 'src/app/models/atlas-request.model';
import { AtlasRequestCommercialAccount } from 'src/app/models/atlas-request-commercial-account.model';
import { AtlasResponse } from 'src/app/models/atlas-response.model';
import { PaymentAuthorizationRequest } from 'src/app/models/icargo/payment-authorization-request.model';
import { PaymentAuthorizationResponse } from 'src/app/models/icargo/payment-authorization-response.model';
import { TransactionResponse } from 'src/app/models/signal-r-result-ipad.model';
import { SharedService } from '../shared-service/shared.service';
import { CargoPaymentInfo } from 'src/app/models/cargo-payment-info.model';
import { AddInvoiceRequest } from 'src/app/models/manual-payment/add-invoice-request.model';
import { AddInvoiceResponse } from 'src/app/models/manual-payment/add-invoice-response.model';
import { Invoice } from 'src/app/models/manual-payment/invoice.model';
import { MonitoringService } from '../monitoring-service/monitoring.service';

@Injectable()
export class PaymentService {
  paymentBaseUrl = '/api/payment';
  paymentAuthorizationUrl = this.paymentBaseUrl + '/paymentAuthorization';
  commercialAccountUrl = this.paymentBaseUrl + '/commercialAccount';
  bluefinUrl = this.paymentBaseUrl + '/bluefin';
  addManualInvoiceUrl = this.paymentBaseUrl + '/manualInvoice';
  getManualInvoiceUrl = this.paymentBaseUrl + '/manualInvoice/?paymentAdviceNumber=';
  transactionId = '';

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(private _http: HttpClient, private sharedService: SharedService, private monitoringService: MonitoringService) {
    this.sharedService.currentTransactionId.subscribe((transactionId) => (this.transactionId = transactionId));
  }

  public sendBluefin(payload: AtlasRequest): Observable<AtlasResponse> {
    return this._http.post<AtlasResponse>(this.bluefinUrl, payload, {
      headers: {
        'Request-Id': this.transactionId,
      },
    });
  }

  public sendCommercialAccount(atlasRequest: AtlasRequestCommercialAccount): Observable<AtlasResponse> {
    return this._http.post<AtlasResponse>(this.commercialAccountUrl, atlasRequest, {
      headers: {
        'Request-Id': this.transactionId,
      },
    });
  }

  public sendPaymentAuthorization(
    transactionResponse: TransactionResponse,
    isTransactionComplete: boolean
  ): Observable<PaymentAuthorizationResponse> {
    const transaction = transactionResponse.transaction;
    const transactionResult = transactionResponse.transactionResult;
    const authorizationCode = transactionResult?.authorizationCode?.toString() || undefined;
    const signature1 = transactionResult.authorizationCode !== null ? transactionResult?.signatureObject1 || undefined : undefined;
    const signature2 = transactionResult.authorizationCode !== null ? transactionResult?.signatureObject2 || undefined : undefined;

    const paymentAuthorizationRequest = new PaymentAuthorizationRequest(
      transaction.paymentAdviceNumber == 'null' ? undefined : transaction.paymentAdviceNumber,
      transaction.paymentAdviceCurrency == 'null' ? undefined : transaction.paymentAdviceCurrency,
      transaction.paymentAdviceAmount,
      transaction.awbNumber,
      transaction.airportCode,
      transaction.operationMode == '' ? undefined : transaction.operationMode,
      authorizationCode,
      transaction.source,
      signature1,
      signature2,
      isTransactionComplete
    );
    return this.paymentAuthorization(paymentAuthorizationRequest);
  }

  public paymentAuthorization(paymentAuthorizationRequest: PaymentAuthorizationRequest): Observable<PaymentAuthorizationResponse> {
    return defer(() => {
      const startedAt = Date.now();
      this.monitoringService.logEvent(
        'CargoPaymentUI.PaymentAuthorization.Started',
        this.createTelemetryProperties(paymentAuthorizationRequest)
      );

      return this._http
        .post<PaymentAuthorizationResponse>(this.paymentAuthorizationUrl, paymentAuthorizationRequest, {
          headers: {
            'Request-Id': this.transactionId,
          },
        })
        .pipe(
          tap((response) => {
            const eventName = response.isSuccess
              ? 'CargoPaymentUI.PaymentAuthorization.Completed'
              : 'CargoPaymentUI.PaymentAuthorization.Failed';
            this.monitoringService.logEvent(
              eventName,
              this.createTelemetryProperties(paymentAuthorizationRequest, {
                durationMs: Date.now() - startedAt,
                outcome: response.isSuccess ? 'success' : 'failed',
                failureCode: response.isSuccess ? undefined : response.errorCode ?? response.responseCode ?? 'unsuccessful-response',
              })
            );
          }),
          catchError((error: unknown) => {
            this.monitoringService.logEvent(
              'CargoPaymentUI.PaymentAuthorization.Failed',
              this.createTelemetryProperties(paymentAuthorizationRequest, {
                durationMs: Date.now() - startedAt,
                httpStatus: this.getHttpStatus(error),
                failureCode: this.getFailureCode(error),
                outcome: 'failed',
              })
            );

            return throwError(() => error);
          })
        );
    });
  }

  public sendAddManualInvoice(cargoPaymentInfo: CargoPaymentInfo): Observable<AddInvoiceResponse> {
    const addInvoiceRequest: AddInvoiceRequest = {
      awbNumber: cargoPaymentInfo.awbNumber,
      customerName: cargoPaymentInfo.customerName,
      paymentAdviceNumber: cargoPaymentInfo.paymentAdviceNumber,
      paymentAdviceAmount: cargoPaymentInfo.paymentAdviceAmount,
      paymentAdviceCurrency: cargoPaymentInfo.paymentAdviceCurrency,
    };
    return this.createManualInvoice(addInvoiceRequest);
  }

  public createManualInvoice(addInvoiceRequest: AddInvoiceRequest): Observable<AddInvoiceResponse> {
    return this._http.post<AddInvoiceResponse>(this.addManualInvoiceUrl, addInvoiceRequest, {
      headers: {
        'Request-Id': this.transactionId,
      },
    });
  }

  public getManualInvoiceToken(paymentAdviceNumber: string): Observable<Invoice> {
    const gatInvoiceUrl = this.getManualInvoiceUrl + paymentAdviceNumber;
    return this._http.get<Invoice>(gatInvoiceUrl, {
      headers: {
        'Request-Id': this.transactionId,
      },
    });
  }

  private createTelemetryProperties(
    paymentAuthorizationRequest: PaymentAuthorizationRequest,
    properties: { [key: string]: unknown } = {}
  ): { [key: string]: unknown } {
    const telemetryProperties = {
      correlationId: this.transactionId || undefined,
      paymentAdviceNumber: paymentAuthorizationRequest.paymentAdviceNumber,
      awb: paymentAuthorizationRequest.awb,
      ...properties,
    };

    return Object.fromEntries(Object.entries(telemetryProperties).filter(([, value]) => value !== undefined && value !== null));
  }

  private getHttpStatus(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }

    if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
      return error.status;
    }

    return undefined;
  }

  private getFailureCode(error: unknown): string | undefined {
    if (error instanceof HttpErrorResponse) {
      const errorBody = error.error as { errorCode?: unknown; code?: unknown } | undefined;

      if (typeof errorBody?.errorCode === 'string') {
        return errorBody.errorCode;
      }

      if (typeof errorBody?.code === 'string') {
        return errorBody.code;
      }

      return error.statusText || String(error.status);
    }

    if (typeof error === 'object' && error !== null) {
      if ('error' in error && typeof error.error === 'object' && error.error !== null && 'errorCode' in error.error) {
        return typeof error.error.errorCode === 'string' ? error.error.errorCode : undefined;
      }

      if ('name' in error && typeof error.name === 'string') {
        return error.name;
      }
    }

    return error instanceof Error ? error.name : undefined;
  }
}
